import React, { useState, useMemo } from 'react';
import ToolLayout from '../components/ToolLayout';

interface DiffSegment {
  value: string;
  type: 'equal' | 'insert' | 'delete';
}

interface DiffResult {
  left: DiffSegment[];
  right: DiffSegment[];
}

const TextDiffChecker: React.FC = () => {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [diffMode, setDiffMode] = useState<'word' | 'character' | 'line'>('word');
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(false);

  // Simple diff algorithm implementation
  const computeDiff = (text1: string, text2: string, mode: 'word' | 'character' | 'line'): DiffResult => {
    let processedText1 = text1;
    let processedText2 = text2;

    if (ignoreCase) {
      processedText1 = text1.toLowerCase();
      processedText2 = text2.toLowerCase();
    }

    if (ignoreWhitespace) {
      processedText1 = text1.replace(/\s+/g, ' ').trim();
      processedText2 = text2.replace(/\s+/g, ' ').trim();
    }

    let segments1: string[];
    let segments2: string[];

    switch (mode) {
      case 'line':
        segments1 = processedText1.split('\n');
        segments2 = processedText2.split('\n');
        break;
      case 'word':
        segments1 = processedText1.split(/(\s+)/);
        segments2 = processedText2.split(/(\s+)/);
        break;
      case 'character':
        segments1 = Array.from(processedText1);
        segments2 = Array.from(processedText2);
        break;
      default:
        segments1 = [processedText1];
        segments2 = [processedText2];
    }

    // Simple LCS-based diff
    const matrix: number[][] = [];
    const m = segments1.length;
    const n = segments2.length;

    // Initialize matrix
    for (let i = 0; i <= m; i++) {
      matrix[i] = new Array(n + 1).fill(0);
    }

    // Fill matrix
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (segments1[i - 1] === segments2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1] + 1;
        } else {
          matrix[i][j] = Math.max(matrix[i - 1][j], matrix[i][j - 1]);
        }
      }
    }

    // Trace back to create diff
    const left: DiffSegment[] = [];
    const right: DiffSegment[] = [];
    let i = m;
    let j = n;

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && segments1[i - 1] === segments2[j - 1]) {
        left.unshift({ value: segments1[i - 1], type: 'equal' });
        right.unshift({ value: segments2[j - 1], type: 'equal' });
        i--;
        j--;
      } else if (i > 0 && (j === 0 || matrix[i - 1][j] >= matrix[i][j - 1])) {
        left.unshift({ value: segments1[i - 1], type: 'delete' });
        right.unshift({ value: '', type: 'delete' });
        i--;
      } else {
        left.unshift({ value: '', type: 'insert' });
        right.unshift({ value: segments2[j - 1], type: 'insert' });
        j--;
      }
    }

    return { left, right };
  };

  const diffResult = useMemo(() => {
    if (!leftText && !rightText) {
      return { left: [], right: [] };
    }
    return computeDiff(leftText, rightText, diffMode);
  }, [leftText, rightText, diffMode, ignoreWhitespace, ignoreCase]);

  const renderDiffSegments = (segments: DiffSegment[], side: 'left' | 'right') => {
    return segments.map((segment, index) => {
      if (segment.value === '') {
        return null;
      }
      
      let className = 'diff-segment ';
      switch (segment.type) {
        case 'insert':
          className += side === 'right' ? 'diff-insert' : 'diff-empty';
          break;
        case 'delete':
          className += side === 'left' ? 'diff-delete' : 'diff-empty';
          break;
        case 'equal':
          className += 'diff-equal';
          break;
      }

      return (
        <span key={index} className={className}>
          {segment.value}
        </span>
      );
    });
  };

  const stats = useMemo(() => {
    const leftStats = {
      total: diffResult.left.filter(s => s.value !== '').length,
      deleted: diffResult.left.filter(s => s.type === 'delete').length,
      equal: diffResult.left.filter(s => s.type === 'equal').length,
    };
    
    const rightStats = {
      total: diffResult.right.filter(s => s.value !== '').length,
      inserted: diffResult.right.filter(s => s.type === 'insert').length,
      equal: diffResult.right.filter(s => s.type === 'equal').length,
    };

    return { left: leftStats, right: rightStats };
  }, [diffResult]);

  const clearInputs = () => {
    setLeftText('');
    setRightText('');
  };

  const swapTexts = () => {
    const temp = leftText;
    setLeftText(rightText);
    setRightText(temp);
  };

  const loadSampleData = () => {
    setLeftText(`Đây là văn bản gốc.
Dòng này sẽ được giữ nguyên.
Dòng này sẽ bị xóa.
Nội dung chung.`);

    setRightText(`Đây là văn bản đã chỉnh sửa.
Dòng này sẽ được giữ nguyên.
Dòng này đã được thêm mới.
Nội dung chung.`);
  };

  return (
    <ToolLayout
      title="Text Diff Checker"
      description="So sánh hai đoạn text và highlight sự khác biệt với side-by-side comparison"
    >
      <div className="diff-checker">
        {/* Controls */}
        <div className="controls-section">
          <div className="control-group">
            <label className="control-label">Chế độ so sánh:</label>
            <div className="button-group">
              <button
                className={`btn ${diffMode === 'character' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDiffMode('character')}
              >
                Ký tự
              </button>
              <button
                className={`btn ${diffMode === 'word' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDiffMode('word')}
              >
                Từ
              </button>
              <button
                className={`btn ${diffMode === 'line' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDiffMode('line')}
              >
                Dòng
              </button>
            </div>
          </div>

          <div className="control-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
              />
              Bỏ qua khoảng trắng
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ignoreCase}
                onChange={(e) => setIgnoreCase(e.target.checked)}
              />
              Bỏ qua chữ hoa/thường
            </label>
          </div>

          <div className="action-buttons">
            <button className="btn btn-secondary" onClick={clearInputs}>
              Xóa tất cả
            </button>
            <button className="btn btn-secondary" onClick={swapTexts}>
              Hoán đổi
            </button>
            <button className="btn btn-primary" onClick={loadSampleData}>
              Tải dữ liệu mẫu
            </button>
          </div>
        </div>

        {/* Statistics */}
        {(leftText || rightText) && (
          <div className="diff-stats">
            <div className="stat-group">
              <h4>Thống kê so sánh</h4>
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-label">Phần bị xóa:</span>
                  <span className="stat-value stat-delete">{stats.left.deleted}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Phần được thêm:</span>
                  <span className="stat-value stat-insert">{stats.right.inserted}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Phần giống nhau:</span>
                  <span className="stat-value stat-equal">{stats.left.equal}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Input Areas */}
        <div className="diff-inputs">
          <div className="input-section">
            <label className="input-label">Text gốc (Trái):</label>
            <textarea
              className="diff-textarea"
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              placeholder="Nhập text gốc vào đây..."
              rows={10}
            />
          </div>

          <div className="input-section">
            <label className="input-label">Text so sánh (Phải):</label>
            <textarea
              className="diff-textarea"
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              placeholder="Nhập text cần so sánh vào đây..."
              rows={10}
            />
          </div>
        </div>

        {/* Diff Result */}
        {(leftText || rightText) && (
          <div className="diff-result">
            <h3>Kết quả so sánh:</h3>
            <div className="diff-container">
              <div className="diff-side">
                <h4>Text gốc</h4>
                <div className="diff-content">
                  {renderDiffSegments(diffResult.left, 'left')}
                </div>
              </div>

              <div className="diff-side">
                <h4>Text so sánh</h4>
                <div className="diff-content">
                  {renderDiffSegments(diffResult.right, 'right')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="diff-legend">
          <h4>Chú thích:</h4>
          <div className="legend-items">
            <span className="legend-item">
              <span className="legend-color diff-insert"></span>
              Nội dung được thêm
            </span>
            <span className="legend-item">
              <span className="legend-color diff-delete"></span>
              Nội dung bị xóa
            </span>
            <span className="legend-item">
              <span className="legend-color diff-equal"></span>
              Nội dung giống nhau
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .diff-checker {
          max-width: 100%;
          margin: 0 auto;
        }

        .controls-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .control-group {
          margin-bottom: 1rem;
        }

        .control-group:last-child {
          margin-bottom: 0;
        }

        .control-label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .button-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .btn-primary {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .btn-secondary {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
          font-weight: normal;
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-top: 1rem;
        }

        .diff-stats {
          background: var(--card-background);
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .diff-stats h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .stats-row {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .stat-label {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .stat-value {
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .stat-delete {
          background: #fee;
          color: #c53030;
        }

        .stat-insert {
          background: #efe;
          color: #38a169;
        }

        .stat-equal {
          background: #f7fafc;
          color: #4a5568;
        }

        .diff-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .diff-inputs {
            grid-template-columns: 1fr;
          }
        }

        .input-section {
          display: flex;
          flex-direction: column;
        }

        .input-label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .diff-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          resize: vertical;
          min-height: 200px;
        }

        .diff-textarea:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .diff-result {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .diff-result h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .diff-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .diff-container {
            grid-template-columns: 1fr;
          }
        }

        .diff-side {
          border: 1px solid var(--border-color);
          border-radius: 6px;
          overflow: hidden;
        }

        .diff-side h4 {
          margin: 0;
          padding: 0.75rem;
          background: var(--input-background);
          border-bottom: 1px solid var(--border-color);
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        .diff-content {
          padding: 1rem;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.6;
          white-space: pre-wrap;
          word-break: break-word;
          max-height: 400px;
          overflow-y: auto;
        }

        .diff-segment {
          border-radius: 2px;
        }

        .diff-insert {
          background-color: #e6ffed;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .diff-delete {
          background-color: #fed7d7;
          color: #742a2a;
          border: 1px solid #fc8181;
          text-decoration: line-through;
        }

        .diff-equal {
          background-color: transparent;
          color: var(--text-primary);
        }

        .diff-empty {
          display: none;
        }

        .diff-legend {
          background: var(--card-background);
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .diff-legend h4 {
          margin: 0 0 0.75rem 0;
          color: var(--text-primary);
          font-size: 0.9rem;
        }

        .legend-items {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 2px;
          border: 1px solid var(--border-color);
        }
      `}</style>
    </ToolLayout>
  );
};

export default TextDiffChecker;
